import React, { useEffect, useState } from 'react';
import { batteriesAPI } from '../../services/garageApi';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Battery,
  AlertCircle,
  CheckCircle,
  XCircle,
  Zap,
  ArrowRightLeft,
  AlertTriangle
} from 'lucide-react';
import ManageStockModal from '../common/ManageStockModal';

const Batteries = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [batteries, setBatteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [batteryToDelete, setBatteryToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [brandFilter, setBrandFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');

  // Manage Stock Modal
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [stockModalData, setStockModalData] = useState(null);

  useEffect(() => {
    // Check for success message from navigation state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from navigation state
      window.history.replaceState({}, document.title);
      // Clear message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    }

    fetchBatteries();
  }, [location]);

  const fetchBatteries = async () => {
    try {
      setLoading(true);
      const res = await batteriesAPI.getAll();

      let batteriesList = [];

      // Handle paginated response structure
      if (res.data) {
        if (res.data.success !== undefined && res.data.data) {
          const responseData = res.data.data;
          if (responseData.data && Array.isArray(responseData.data)) {
            batteriesList = responseData.data;
          } else if (Array.isArray(responseData)) {
            batteriesList = responseData;
          }
        } else if (res.data.data && Array.isArray(res.data.data)) {
          batteriesList = res.data.data;
        } else if (Array.isArray(res.data)) {
          batteriesList = res.data;
        }
      }

      // Process each battery to add computed fields
      batteriesList = batteriesList.map(battery => {
        // Add quantity from inventories if available
        if (battery.inventories && Array.isArray(battery.inventories)) {
          battery.quantity = battery.inventories.reduce((sum, inv) => sum + (inv.quantity || 0), 0);
        }
        battery.unallocated_quantity = parseInt(battery.unallocated_quantity) || 0;
        battery.has_unallocated = battery.has_unallocated || false;
        return battery;
      });

      setBatteries(batteriesList);
      setError('');
    } catch (err) {
      console.error('Error fetching batteries:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || err.message || 'Failed to load batteries');
      setBatteries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!batteryToDelete) return;

    try {
      await batteriesAPI.delete(batteryToDelete.id);
      setSuccessMessage('Battery deleted successfully!');
      fetchBatteries();
      setShowDeleteModal(false);
      setBatteryToDelete(null);
    } catch (err) {
      console.error('Error deleting battery:', err);
      alert('Error deleting battery. Please try again.');
    }
  };

  const confirmDelete = (battery) => {
    setBatteryToDelete(battery);
    setShowDeleteModal(true);
  };

  const getStockStatus = (battery) => {
    // Try multiple ways to get quantity
    const qty = parseInt(
        battery.quantity ||
        battery.inventory_quantity ||
        battery.total_quantity ||
        (battery.inventories && battery.inventories[0]?.quantity) ||
        0
    );

    if (qty === 0) return { text: 'Out of Stock', color: 'red', icon: XCircle };
    if (qty < 10) return { text: 'Low Stock', color: 'yellow', icon: AlertCircle };
    return { text: 'In Stock', color: 'green', icon: CheckCircle };
  };

  const getQuantity = (battery) => {
    return battery.quantity ||
        battery.inventory_quantity ||
        battery.total_quantity ||
        (battery.inventories && battery.inventories[0]?.quantity) ||
        0;
  };

  const getBrandName = (battery) => {
    if (battery.battery_brand?.name) return battery.battery_brand.name;
    if (battery.batteryBrand?.name) return battery.batteryBrand.name;
    if (battery.brand?.name) return battery.brand.name;
    if (battery.brand_name) return battery.brand_name;
    return 'Unknown Brand';
  };

  const getMadeIn = (battery) => {
    if (battery.made_in?.name) return battery.made_in.name;
    if (battery.madeIn?.name) return battery.madeIn.name;
    if (battery.origin) return battery.origin;
    return 'N/A';
  };

  const getBatteryType = (battery) => {
    if (typeof battery.battery_type === 'object' && battery.battery_type?.name) return battery.battery_type.name;
    if (typeof battery.battery_type === 'string') return battery.battery_type;
    if (battery.batteryType?.name) return battery.batteryType.name;
    return 'N/A';
  };

  const filteredBatteries = batteries.filter(battery => {
    const searchLower = searchTerm.toLowerCase();
    const brandName = getBrandName(battery);

    const typeName = getBatteryType(battery);

    const matchesSearch = !searchTerm || (
        battery.battery_name?.toLowerCase().includes(searchLower) ||
        brandName.toLowerCase().includes(searchLower) ||
        typeName.toLowerCase().includes(searchLower) ||
        battery.ampere?.toString().includes(searchLower)
    );

    const matchesBrand = !brandFilter || brandName === brandFilter;
    const matchesType = !typeFilter || typeName === typeFilter;

    let matchesStock = true;
    if (stockFilter) {
      const qty = parseInt(getQuantity(battery));
      if (stockFilter === 'in') matchesStock = qty >= 10;
      else if (stockFilter === 'low') matchesStock = qty > 0 && qty < 10;
      else if (stockFilter === 'out') matchesStock = qty === 0;
    }

    return matchesSearch && matchesBrand && matchesType && matchesStock;
  });

  if (loading) {
    return (
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );
  }

  if (error && batteries.length === 0) {
    return (
        <div className="m-4 rounded-lg border border-red-400 bg-red-50 px-4 py-3 text-red-700">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>Error loading batteries: {error}</span>
          </div>
          <button
              onClick={fetchBatteries}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Success Message */}
        {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-green-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span>{successMessage}</span>
            </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Batteries</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage your battery inventory ({batteries.length} total)
              </p>
            </div>
            <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                onClick={() => navigate('/batteries/create')}
            >
              <Plus className="w-4 h-4" />
              Add Battery
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                  type="text"
                  placeholder="Search batteries by name, brand, type, or ampere..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 border rounded-lg transition-colors flex items-center gap-2 ${
                    showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 hover:bg-gray-50'
                }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <select
                      value={brandFilter}
                      onChange={(e) => setBrandFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All Brands</option>
                    {[...new Set(batteries.map(b => getBrandName(b)).filter(b => b !== 'Unknown Brand'))].sort().map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                  <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All Types</option>
                    {[...new Set(batteries.map(b => getBatteryType(b)).filter(t => t !== 'N/A'))].sort().map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <select
                      value={stockFilter}
                      onChange={(e) => setStockFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All Stock Status</option>
                    <option value="in">In Stock</option>
                    <option value="low">Low Stock</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
                {(brandFilter || typeFilter || stockFilter) && (
                    <button
                        onClick={() => { setBrandFilter(''); setTypeFilter(''); setStockFilter(''); }}
                        className="mt-3 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      Reset Filters
                    </button>
                )}
              </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Battery Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Brand & Origin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Specifications
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Power
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
              {filteredBatteries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <Battery className="w-12 h-12 text-gray-400 mb-3" />
                        <p className="text-gray-500">
                          {searchTerm ? 'No batteries found matching your search' : 'No batteries found'}
                        </p>
                        <button
                            onClick={() => navigate('/batteries/create')}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Add Your First Battery
                        </button>
                      </div>
                    </td>
                  </tr>
              ) : (
                  filteredBatteries.map((battery) => {
                    const stockStatus = getStockStatus(battery);
                    const StockIcon = stockStatus.icon;
                    const quantity = getQuantity(battery);

                    return (
                        <tr key={battery.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Battery className="w-6 h-6 text-blue-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {battery.battery_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: #{battery.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {getBrandName(battery)}
                              </div>
                              <div className="text-sm text-gray-500">
                                Made in {getMadeIn(battery)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div>
                                <span className="capitalize">{getBatteryType(battery)}</span>
                              </div>
                              <div className="text-sm text-gray-500">
                            <span className="capitalize">
                              {battery.battery_shape || battery.product_shape || 'N/A'}
                            </span>
                                {battery.reversed ? ' • Reversed' : ''}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <Zap className="w-4 h-4 text-yellow-500" />
                              <span className="text-sm font-medium text-gray-900">
                            {battery.ampere} AH
                          </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <StockIcon className={`w-4 h-4 text-${stockStatus.color}-500`} />
                              <div>
                            <span className={`text-sm font-medium text-${stockStatus.color}-600`}>
                              {quantity} units
                            </span>
                                <div className={`text-xs text-${stockStatus.color}-600`}>
                                  {stockStatus.text}
                                </div>
                                {battery.has_unallocated && (
                                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                      <AlertTriangle className="w-3 h-3" />
                                      Pending Allocation
                                    </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">
                                ${parseFloat(battery.selling_price || 0).toFixed(2)}
                              </div>
                              {battery.cost_price && (
                                  <div className="text-xs text-gray-500">
                                    Cost: ${parseFloat(battery.cost_price).toFixed(2)}
                                  </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <Link
                                  to={`/batteries/${battery.id}`}
                                  className="text-gray-600 hover:text-gray-900 p-1 hover:bg-gray-100 rounded transition-colors"
                                  title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                              <Link
                                  to={`/batteries/${battery.id}/edit`}
                                  className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </Link>
                              <button
                                  onClick={() => {
                                    setStockModalData({ productIds: [battery.id], productLabel: battery.battery_name, productType: 'battery' });
                                    setStockModalOpen(true);
                                  }}
                                  className={`p-1 rounded transition-colors flex items-center gap-1 ${
                                      battery.has_unallocated
                                          ? 'text-yellow-700 hover:bg-yellow-50'
                                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                  }`}
                                  title="Manage Stock"
                              >
                                {battery.has_unallocated && <AlertTriangle className="w-3 h-3" />}
                                <ArrowRightLeft className="w-4 h-4" />
                              </button>
                              <button
                                  onClick={() => confirmDelete(battery)}
                                  className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                    );
                  })
              )}
              </tbody>
            </table>
          </div>

          {/* Table Footer with Count */}
          {filteredBatteries.length > 0 && (
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {filteredBatteries.length} of {batteries.length} batteries
                </div>
              </div>
          )}
        </div>

        {/* Delete Modal */}
        {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Battery</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete "{batteryToDelete?.battery_name}"? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setBatteryToDelete(null);
                      }}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                      onClick={handleDelete}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* Manage Stock Modal */}
        <ManageStockModal
            isOpen={stockModalOpen}
            onClose={() => {
              setStockModalOpen(false);
              setStockModalData(null);
            }}
            productIds={stockModalData?.productIds || []}
            productLabel={stockModalData?.productLabel || ''}
            productType="battery"
            onStockMoved={() => fetchBatteries()}
        />
      </div>
  );
};

export default Batteries;